import type { PlanOut } from '@/api/schema';
import { int } from '@/lib/format';

/*
 * A plan's pitch line and short list, shared by the plans page's cards and the upgrade prompt
 * (components/domain/UpgradePrompt) so the two can never describe a plan differently.
 */

/** A day count as a reader says it: whole years as years, anything else as days. */
export function span(days: number) {
  if (days >= 365 && days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  return `${int(days)} days`;
}

/**
 * One line under each plan's name. **What the plan lets you do, never what it achieves**
 * (`marketing/compliance.md`): no plan "finds" or "wins" anything. Unknown codes get none.
 */
export const PITCH: Record<string, string> = {
  free: 'Build and test your first strategies',
  pro: 'Every indicator and every timeframe',
  elite: 'Every feature, US stocks and ETFs, and the most credits',
};

/**
 * The card's short list, read off the plan's own features so it cannot disagree with the table:
 * the limits a buyer compares first, then the switches this plan turns on.
 */
export function keyPoints(plan: PlanOut, cryptoOnly: boolean): string[] {
  const f = (code: string) => plan.features?.find((x) => x.code === code);
  const out: string[] = [];
  const strategies = f('strategies')?.limit;
  const watchlists = f('watchlists')?.limit;
  if (strategies != null) {
    out.push(`${int(strategies)} strategies${watchlists != null ? `, ${int(watchlists)} watchlists` : ''}`);
  }
  const credits = f('strategy_credits_per_week')?.limit;
  if (credits != null) out.push(`${int(credits)} credits a week`);
  const history = f('backtest_days')?.limit;
  if (history != null) out.push(`${span(history)} of backtest history`);
  const indicators = f('full_indicators');
  if (indicators) out.push(indicators.enabled ? indicators.name : 'Core indicator set');
  const tf = plan.timeframes ?? [];
  out.push(tf.length ? `${tf.length} timeframes` : 'Every timeframe');
  for (const code of ['equities', 'multi_timeframe', 'optional_rules', 'deep_preview']) {
    // A crypto-only country holds no stocks on any plan, so Elite must not list them.
    if (code === 'equities' && cryptoOnly) continue;
    const feature = f(code);
    if (feature?.enabled) out.push(feature.name);
  }
  return out;
}
